/**
 * Help-drawer content — single source of truth for in-app help.
 * Mirrors /app/memory/USER_GUIDE.md but trimmed to the bits a user
 * actually needs while in-app. Keep it scannable: short paragraphs, lists.
 */
export const HELP_SECTIONS = [
    {
        slug: 'getting-started',
        title: 'Getting started',
        icon: 'Sparkle',
        // Auto-expanded for Foundation users on first open
        defaultFor: { tier: 'foundation', route: '/' },
        body: [
            { type: 'p', text: 'DefectR turns you into a competent snagger with builder-grade output. Three steps:' },
            { type: 'ol', items: [
                'Add a property and the rooms inside it',
                'Capture defects with a photo, notes, and (optionally) a pin on the floorplan',
                'Send a clean PDF — or live share link — to your builder',
            ]},
            { type: 'p', text: 'Everything is saved automatically. You can sign back in from any device with the same Google account.' },
        ],
    },
    {
        slug: 'tiers',
        title: 'Tiers & pricing',
        icon: 'Crown',
        body: [
            { type: 'table', headers: ['Tier', 'Cost', 'Property cap', 'Snag cap', 'Share link', 'Full AI analysis', 'Walk-around AI'], rows: [
                ['Foundation', 'Free', '1', '10', '✗', '3 lifetime', '✗'],
                ['Rectify', '£14.99 / property', 'Unlimited (each unlock)', 'Unlimited on unlocked', '✓', 'Unlimited on unlocked', '✓ on unlocked'],
                ['Surveyor', '£149.99 lifetime', 'Unlimited', 'Unlimited', '✓', 'Unlimited', '✓'],
                ['Architect', 'Invite-only', 'Unlimited', 'Unlimited', '✓', 'Unlimited', '✓'],
                ['Developer (B2B)', '£4,800 / year', 'Unlimited team-wide', 'Unlimited', '✓', 'Unlimited', '✓'],
                ['Enterprise', 'Custom', 'Unlimited', 'Unlimited', '✓', 'Unlimited', '✓'],
            ]},
            { type: 'p', text: 'Whenever you hit a free-tier cap, an upgrade prompt appears with the right context — no need to hunt for a pricing page. For housebuilders running snagging at scale, see the Developer-tier section below.' },
        ],
    },
    {
        slug: 'capturing-defects',
        title: 'Capturing defects',
        icon: 'Camera',
        defaultFor: { route: '/app/project' },
        body: [
            { type: 'p', text: 'Three ways to add a defect:' },
            { type: 'ul', items: [
                'Log new defect — the slow path. Pick room → photo → save. Use when each defect needs proper notes / severity.',
                'Walk-around — sped-up flow for the first walk-through. Hit "Walk-around" on a property, the camera fires immediately, every photo becomes a queued defect (~2 taps each), save the batch when done. Per-photo room dropdown lets you re-assign on the fly.',
                'By tapping the floorplan — drops a numbered pin at that exact spot.',
            ]},
            { type: 'note', text: 'Walk-around AI Suggest (title + severity from the photo) is a paid feature — Rectify (£14.99 per property) or Surveyor (£149.99 lifetime). Foundation users can still walk-around manually and tag each photo themselves.' },
            { type: 'p', text: 'Photo markup: tap "Mark up" on any defect photo to overlay red arrows, circles, freehand strokes or text labels. The markup shows on PDF reports AND on the builder share portal — far harder for a builder to ignore than a wide shot of a wall.' },
            { type: 'note', text: 'Annotated thumbnails get a small cyan ✎ corner badge so you can spot which defects have markup at a glance.' },
            { type: 'p', text: 'Tips:' },
            { type: 'ul', items: [
                'One defect per photo — clearer for the builder, better for AI analysis',
                'Pin while you\'re in the room — coming back to "where was that?" is annoying',
                'Click any defect row to toggle Outstanding ↔ Completed',
            ]},
        ],
    },
    {
        slug: 'floorplans',
        title: 'Floorplans',
        icon: 'MapTrifold',
        defaultFor: { route: '/app/floorplan' },
        body: [
            { type: 'p', text: 'Upload one floorplan per floor. Supported formats:' },
            { type: 'ul', items: [
                'PNG / JPEG / WEBP — works at any resolution; ≥2000px on the long edge gives a print-sharp PDF',
                'SVG — recommended for architects\' drawings; scales perfectly at any size',
            ]},
            { type: 'p', text: 'Tap any spot on the plan to drop a pin and start a new defect at that location. Pins auto-number from 1 upward and stay tied to the defect even if you rename it.' },
            { type: 'p', text: 'Pin colours: cyan = outstanding · red = builder rejected · green = completed. Severity ring: amber = major · dark maroon = urgent.' },
            { type: 'p', text: 'Jumping between defects and pins:' },
            { type: 'ul', items: [
                'From a defect row (Home tab) tap Show pin to locate the pin on the plan — read-only, briefly pulses so you can find it on a dense floor. Or tap Move pin to reposition it.',
                'From the Pins-on-this-floor list on the Plan page, each row now shows Edit (jump to that defect\'s edit form on the Home tab) and Move (enter placement mode).',
                'Deep-link URL — /app/project/<propertyId>?snag=<snagId> opens the property with that snag\'s edit form pre-opened. Useful when a builder emails you about a specific defect.',
            ]},
        ],
    },
    {
        slug: 'tools',
        title: 'Spirit level & measure',
        icon: 'Wrench',
        defaultFor: { route: '/app/tools' },
        body: [
            { type: 'p', text: 'Both tools work on a phone — they use your phone\'s sensors and screen.' },
            { type: 'p', text: 'Spirit level: place phone flat on the surface you\'re checking. Tap Calibrate if you want the current orientation to be "level zero" (e.g. checking a deliberately-sloped shower floor).' },
            { type: 'p', text: 'Measure tool: hold a credit card against the screen, drag the slider until they line up — that calibrates the on-screen ruler. Best for gaps under 100mm; a real tape measure beats it for anything bigger.' },
        ],
    },
    {
        slug: 'ai-analysis',
        title: '✨ AI snag analysis',
        icon: 'Sparkle',
        defaultFor: { tier: ['rectify', 'surveyor', 'architect'] },
        body: [
            { type: 'p', text: 'When a defect has at least a title (and ideally a photo), click "AI: What\'s this?" to get an instant verdict from a vision AI:' },
            { type: 'ul', items: [
                'Likely cause — plain English explanation',
                'Severity — Cosmetic / Minor / Major / Urgent',
                'Suggested fix — DIY or call-the-builder',
                'Cost band — £ / ££ / £££',
                'NHBC reference — when applicable, useful when negotiating',
            ]},
            { type: 'p', text: 'There are TWO AI features:' },
            { type: 'ul', items: [
                'Full defect analysis (5-field verdict above) — Foundation gets 3 free lifetime uses. Rectify/Surveyor unlimited on unlocked properties.',
                'Walk-around AI Suggest (title + severity only) — PAID feature, no free tier. Available on Rectify (£14.99 per property) or Surveyor (£149.99 lifetime).',
            ]},
            { type: 'p', text: 'Verdicts auto-include in your PDF and the share link your builder sees — they\'re much harder to dispute when they cite a recognised standard. On mobile, the Cause is always visible with Fix / Cost / Who behind a "Show details" toggle.' },
            { type: 'note', text: 'AI estimate — not a structural assessment. Always consult a qualified professional for urgent issues.' },
        ],
    },
    {
        slug: 'sending-the-report',
        title: 'Sending the report',
        icon: 'PaperPlaneTilt',
        defaultFor: { route: '/app/report' },
        body: [
            { type: 'p', text: 'Three ways to ship your snag list:' },
            { type: 'ol', items: [
                'Download PDF — branded report with snag list, photos, AI verdicts and pin-overlaid floorplans. Includes a QR code if you\'ve built a share link.',
                'Email to builder — sends the same PDF as a branded email from reports@defectr.co.uk; replies go straight back to you.',
                'Build share link — generates a public URL the builder can open and reply on.',
            ]},
            { type: 'p', text: 'Tip: build the share link FIRST, then download the PDF — that way the QR code in the PDF takes the builder straight to the live reply portal.' },
            { type: 'p', text: 'Stuck on a builder rejection? If any of your defects are rejected or disputed, a blue "Open NHBC Claim Form" launchpad appears on the Report page with a "Have these ready" checklist (your PDF, policy number, builder name, dispute count). DefectR doesn\'t auto-submit anything — the NHBC survey stays in your hands.' },
        ],
    },
    {
        slug: 'builder-responses',
        title: 'Builder responses & disputes',
        icon: 'ChatCircleDots',
        body: [
            { type: 'p', text: 'Once you share your report, the builder can respond to every defect — and you can push back if you disagree.' },
            { type: 'ul', items: [
                'Approve — builder agrees to fix. Strip turns cyan, stamped with their name and date.',
                'Reject — builder declines with a reason (Normal settlement / Wear & tear / NHBC tolerance / Out of warranty / Cosmetic / Out of scope / Other) plus optional 600-char detail. Strip turns amber.',
                'Dispute rejection — your move when a rejection is unfair. Tap the amber strip on any rejected defect, write up to 1000 chars (cite NHBC chapter, photos taken on completion day, prior agreement…) and submit. Strip goes blue.',
                'Withdraw dispute — change your mind after a chat with the builder? One tap reverses your dispute.',
            ]},
            { type: 'p', text: 'You\'ll get one digest email per ~30 mins summarising builder changes — bulk approvals never spam you. The "Disputes & Rejections" appendix at the back of every PDF gives an NHBC adjudicator or surveyor a one-page audit trail.' },
            { type: 'note', text: 'You always own the "Completed" toggle. Builder approvals are advisory — they can\'t mark a defect fixed without you confirming.' },
        ],
    },
    {
        slug: 'builder-portal',
        title: 'The builder reply portal',
        icon: 'ShareFat',
        body: [
            { type: 'p', text: 'When your builder opens the share link, they see a clean mobile page with:' },
            { type: 'ul', items: [
                'Outstanding & completed defect lists with photos and AI verdicts',
                'Tappable photos that open full-screen',
                'Floorplans with the same pins you placed',
                'A cyan map-pin button on each row — tap it to jump to the matching pin and pulse it',
            ]},
            { type: 'p', text: 'They can mark items "Fixed" or "Reopen" then send the changes back as a QR code. You scan it from Report → "Scan builder reply" and your snag list updates instantly. No builder signup required.' },
        ],
    },
    {
        slug: 'troubleshooting',
        title: 'Troubleshooting',
        icon: 'Lifebuoy',
        body: [
            { type: 'table', headers: ['Symptom', 'Fix'], rows: [
                ['Photo upload spins forever', 'Photo > 4 MB — take a new one or compress'],
                ['Floorplan looks blurry in PDF', 'Re-upload at ≥2000px or as SVG'],
                ['QR scanner says camera unavailable', 'Allow camera in browser settings, or paste the reply code manually'],
                ['Email to builder bounces', 'Check the address; try the share link instead'],
                ['Stripe paid but still locked', 'Wait 30s & refresh — webhook can briefly lag'],
                ['Mark Fixed doesn\'t stick', 'Builder is on a stale share link — ask homeowner to rebuild it'],
                ['Walk-around says "still processing"', 'One of your photos is large — wait for the spinner to clear before tapping Save'],
                ['Architect invite link did nothing', 'Resolved 2026-05-19 — click your invite link again, it now works correctly through the Google sign-in round-trip'],
            ]},
            { type: 'p', text: 'Anything else? Email support@defectr.co.uk with a screenshot.' },
        ],
    },
    {
        slug: 'badges-and-nudges',
        title: 'In-app badges & nudges',
        icon: 'Sparkle',
        body: [
            { type: 'p', text: 'You\'ll see a few subtle prompts as you use DefectR — every one is dismissable and never repeats more than necessary:' },
            { type: 'ul', items: [
                '🔓 Just unlocked — small cyan chip next to a property name for 24h after a successful Rectify or Surveyor unlock. Welcome to the next tier.',
                '💌 Loving DefectR? — one-off referral nudge that appears 24h to 30 days after a Rectify unlock. Tap Share to fire your phone\'s native share sheet (or copies a friendly message to clipboard on desktop). Tap "Not now" and it won\'t pester you for 30 days.',
                '✎ Annotation badge — small cyan corner badge on photo thumbnails that carry vector markup (red arrows / circles).',
                '✨ AI suggestions — appear inline on a defect once you\'ve run "What\'s this?" — never auto-fire to avoid burning your AI allowance.',
            ]},
        ],
    },
    {
        slug: 'privacy-and-deletion',
        title: 'Privacy & account deletion',
        icon: 'Shield',
        body: [
            { type: 'p', text: 'DefectR is built around UK GDPR. Two specific rights are one-tap from /app/account:' },
            { type: 'ul', items: [
                'Download my data (Article 20) — JSON export of every property, defect, room, floorplan reference, builder link and payment-receipt record. Never your card number.',
                'Delete my account (Article 17) — irreversible. Wipes your Mongo records AND the photo / floorplan / team-logo blobs in object storage. Returns counts of blobs wiped so you can audit the action.',
            ]},
            { type: 'p', text: 'You also get one-tap sign-out across every device from the same page.' },
            { type: 'note', text: 'Account deletion sweeps team-branding logos uploaded as a Developer-tier admin, but does NOT cancel an active Stripe subscription. Cancel that first via /team → Manage billing.' },
        ],
    },
    {
        slug: 'developer-tier',
        title: 'For housebuilders (Developer tier)',
        icon: 'Crown',
        defaultFor: { tier: 'developer_team', route: '/team' },
        body: [
            { type: 'p', text: 'The Developer tier (£4,800/yr, 25 seats) is for housebuilders running snagging across many plots. Sign up at /developer; manage your team from /team:' },
            { type: 'ul', items: [
                '/developer — public marketing & Stripe annual-subscription signup (anyone can open it)',
                '/team — admin dashboard for invites, branding, CSV import and billing (sign-in required)',
                '/team/accept?token=… — magic-link landing for invited seats',
                'Invite seats by email — magic-link sign-in, no passwords. Up to 25 seats; revoke any in one click.',
                'Branded PDF reports — upload your logo and pick a brand colour. Every report goes out under YOUR identity.',
                'CSV bulk import — drop a CSV of plot_number,address rows and DefectR creates every property in one go.',
                'Daily digest — each linked builder gets one summary email per day at 08:00 UK time covering new defects.',
                'Billing via Stripe — annual recurring subscription, cancel from the customer portal any time.',
            ]},
            { type: 'p', text: 'Need more than 25 seats? Email support@defectr.co.uk for an Enterprise quote — same features, custom seat count, off-platform invoice if you need a PO.' },
            { type: 'note', text: 'Seats inherit unlimited access (unlimited properties, defects, AI, share links, branded PDFs) for the duration of the subscription.' },
        ],
    },
];

/** Resolve which section to auto-expand based on current route + user tier.
 *  Falls back to "getting-started" when nothing matches. */
export function pickDefaultSlug(pathname, tier) {
    for (const sec of HELP_SECTIONS) {
        const d = sec.defaultFor;
        if (!d) continue;
        const tierMatch = !d.tier
            ? true
            : Array.isArray(d.tier)
                ? d.tier.includes(tier)
                : d.tier === tier;
        const routeMatch = !d.route ? true : (pathname || '').startsWith(d.route);
        if (tierMatch && routeMatch && (d.tier || d.route)) return sec.slug;
    }
    return 'getting-started';
}
